import React, { FC } from 'react';
import { Link } from 'react-router-dom';

import Utils from '../utils';
import MetadataUtils from '../utils/utils-metadata';
import { Narrator } from '../../../models/Product';
import { useLanguage } from '../../contexts/language';
import { useTranslation } from 'react-i18next';
import { useEnvironment } from '../components/EnvironmentContext';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../utils/togglz';
import { useAgeCategorySearchParam } from '../utils/useAgeCategorySearchParam';

type Props = {
    readonly product?: {
        productClass?: string;
        narrators?: ReadonlyArray<Narrator>;
    };
    readonly loanFormatKey: string | null | undefined;
    readonly showAsTile?: boolean;
    readonly shopLanguage: string | undefined;
};

export const ProductNarrators: FC<Props> = ({ product = {}, loanFormatKey, showAsTile = false, shopLanguage }) => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { productClass, narrators } = product;
    const language = useLanguage();
    const env = useEnvironment();
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    if (!narrators) {
        return null;
    }

    // TODO: extract a separate component for this use case.
    //  It seems to be used on the product details page.
    if (showAsTile) {
        return (
            <div className="narrators">
                {narrators.map(narrator => {
                    const performerPhotoUrl = MetadataUtils.performerPhotoUrl(narrator.id ?? '', env);
                    const linkTo = createNarratorsLink({
                        productClass,
                        narrator,
                        loanFormatKey,
                        language,
                        isMultiLanguageShopEnabled,
                        shopLanguage,
                        ageCategory: ageCategorySearchParam
                    });

                    return (
                        <Link key={narrator.id} className="person" to={linkTo}>
                            <div className="photo" style={{ backgroundImage: `url(${performerPhotoUrl})` }} />
                            <div className="info">
                                <span className="title">{t('marketing.yourCampaigns.narrator')}</span>
                                <span className="name">{MetadataUtils.getPerformerFullname(narrator)}</span>
                            </div>
                        </Link>
                    );
                })}
            </div>
        );
    }

    const filteredNarrators = Utils.filterUntilTextLength(product.narrators ?? [], {
        getTextFunction: narrator => `${MetadataUtils.getPerformerFullname(narrator)}, `,
        initialText: `${t('marketing.yourCampaigns.readBy')}{' '}`
    });

    const allShown = filteredNarrators.length === narrators.length;

    //TODO: [Localisation] prevent string concatenation
    return (
        <p>
            {t('marketing.yourCampaigns.readBy')}{' '}
            {Utils.safeJoinComponents({
                elements: filteredNarrators.map(narrator => {
                    if (narrator.id) {
                        const linkTo = createNarratorsLink({
                            productClass,
                            narrator,
                            loanFormatKey,
                            language,
                            isMultiLanguageShopEnabled,
                            shopLanguage,
                            ageCategory: ageCategorySearchParam
                        });
                        return (
                            <Link key={narrator.id} to={linkTo}>
                                {MetadataUtils.getPerformerFullname(narrator)}
                            </Link>
                        );
                    }
                    return MetadataUtils.getPerformerFullname(narrator);
                }),
                separator: ', ',
                tag: 'span'
            })}
            {!allShown ? ',...' : null}
        </p>
    );
};

export default ProductNarrators;

interface NarratorsLinkParams {
    productClass: string | undefined;
    loanFormatKey: string | null | undefined;
    language: string;
    shopLanguage: string | undefined;
    isMultiLanguageShopEnabled: boolean;
    narrator: Narrator;
    ageCategory: string | undefined;
}
function createNarratorsLink({
    productClass,
    narrator,
    loanFormatKey,
    language,
    shopLanguage,
    isMultiLanguageShopEnabled,
    ageCategory
}: NarratorsLinkParams) {
    const filter = {
        narrator: { include: [narrator.id] }
    };
    return MetadataUtils.createMetaDataLink({
        productClass,
        filter,
        extra: {
            loanFormatKey,
            language
        },
        shopLanguage,
        isMultiLanguageShopEnabled,
        ageCategory
    });
}
